import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { adminOrderService } from '../../services'
import PaymentProofBankBranchFields from './PaymentProofBankBranchFields'
import {
  PAYMENT_METHODS,
  buildPaymentMethodChangePatch,
  buildProofSubmitPayload,
  pendingProofFromRejectedApiProof,
  isBkashPaymentMethod,
  isCashDepositPaymentMethod,
  isChequePaymentMethod,
  requiresDepositorBankAccount,
  sanitizeAmountInput,
  validatePendingProof,
} from '../../utils/paymentProofFormState'

function mergeReceivingAccountsWithProofs(accounts, apiProofs) {
  const byId = new Map((accounts || []).map((row) => [String(row.id), row]))
  for (const proof of apiProofs || []) {
    const id = proof?.shwapno_payment_settings_id
    if (id == null || byId.has(String(id))) continue
    const label = String(proof.shwapno_receiving_account_label || '').trim()
    const [bankName, accountNumber] = label.split(' · ')
    byId.set(String(id), {
      id,
      bank_name: bankName || 'Selected account',
      account_number: accountNumber || label || String(id),
    })
  }
  return [...byId.values()]
}

function ProofFields({
  proof,
  index,
  banks,
  shwapnoAccounts,
  onPatch,
  disabled,
  showFieldErrors = false,
}) {
  const validShwapnoIds = shwapnoAccounts.map((account) => account.id)
  const fieldErrors = showFieldErrors
    ? validatePendingProof(proof, new Date(), {
        validShwapnoPaymentSettingsIds: validShwapnoIds,
      })
    : {}
  const method = String(proof.paymentMethod || '').toUpperCase()
  const showBkash = isBkashPaymentMethod(method)
  const showCheque = isChequePaymentMethod(method)
  const showCashDeposit = isCashDepositPaymentMethod(method)
  const showDepositorBank = requiresDepositorBankAccount(method)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
      <Typography variant="subtitle2">{proof.file?.name || `Proof ${index + 1}`}</Typography>
      <FormControl size="small" fullWidth error={Boolean(fieldErrors.paymentMethod)}>
        <InputLabel>Payment method</InputLabel>
        <Select
          label="Payment method"
          value={proof.paymentMethod || ''}
          onChange={(e) =>
            onPatch(
              index,
              buildPaymentMethodChangePatch(proof.paymentMethod, e.target.value),
            )
          }
          disabled={disabled}
        >
          {PAYMENT_METHODS.map((m) => (
            <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
          ))}
        </Select>
      </FormControl>
      {showCashDeposit ? (
        <Typography variant="caption" color="text.secondary">
          Cash deposit: depositor bank account details are not required.
        </Typography>
      ) : null}
      {showBkash ? (
        <>
          <TextField
            size="small"
            label="bKash phone no"
            value={proof.bkashPhone || ''}
            onChange={(e) =>
              onPatch(index, { bkashPhone: e.target.value.replace(/\D/g, '').slice(0, 11) })
            }
            error={Boolean(fieldErrors.bkashPhone)}
            helperText={fieldErrors.bkashPhone || '11 digits, starts with 0'}
            disabled={disabled}
          />
          <TextField
            size="small"
            label="Transaction ID"
            placeholder="01XXXXXXXXX"
            value={proof.bkashTransactionId || ''}
            onChange={(e) =>
              onPatch(index, {
                bkashTransactionId: e.target.value.replace(/[^A-Za-z0-9]/g, ''),
              })
            }
            error={Boolean(fieldErrors.bkashTransactionId)}
            helperText={fieldErrors.bkashTransactionId || 'Alphanumeric'}
            disabled={disabled}
          />
        </>
      ) : null}
      {showCheque ? (
        <>
          <TextField
            size="small"
            label="Cheque no"
            value={proof.chequeNumber || ''}
            onChange={(e) => onPatch(index, { chequeNumber: e.target.value.slice(0, 10) })}
            error={Boolean(fieldErrors.chequeNumber)}
            helperText={fieldErrors.chequeNumber || '3–10 characters'}
            disabled={disabled}
          />
          <TextField
            size="small"
            label="Cheque date"
            type="date"
            value={proof.chequeDate || ''}
            onChange={(e) => onPatch(index, { chequeDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
            error={Boolean(fieldErrors.chequeDate)}
            helperText={fieldErrors.chequeDate || 'Required'}
            disabled={disabled}
          />
        </>
      ) : null}
      {showDepositorBank ? (
        <>
          <PaymentProofBankBranchFields
            banks={banks}
            proof={proof}
            errors={fieldErrors}
            disabled={disabled}
            onPatch={(patch) => onPatch(index, patch)}
          />
          <TextField
            size="small"
            label="Account number"
            value={proof.accountNumber || ''}
            onChange={(e) =>
              onPatch(index, {
                accountNumber: e.target.value.replace(/\D/g, '').slice(0, 17),
              })
            }
            error={Boolean(fieldErrors.accountNumber)}
            helperText={fieldErrors.accountNumber}
            disabled={disabled}
          />
        </>
      ) : null}
      <TextField
        size="small"
        label="Amount paid (৳)"
        inputProps={{ inputMode: 'decimal' }}
        value={proof.amount || ''}
        onChange={(e) => onPatch(index, { amount: sanitizeAmountInput(e.target.value) })}
        error={Boolean(fieldErrors.amount)}
        helperText={fieldErrors.amount}
        disabled={disabled}
      />
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          label="Payment date"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={proof.paymentDate || ''}
          onChange={(e) => onPatch(index, { paymentDate: e.target.value })}
          error={Boolean(fieldErrors.paymentDate)}
          helperText={fieldErrors.paymentDate}
          sx={{ flex: 1, minWidth: 160 }}
          disabled={disabled}
        />
        <TextField
          size="small"
          label="Payment time"
          type="time"
          InputLabelProps={{ shrink: true }}
          value={proof.paymentTime || ''}
          onChange={(e) => onPatch(index, { paymentTime: e.target.value })}
          error={Boolean(fieldErrors.paymentTime)}
          helperText={fieldErrors.paymentTime}
          sx={{ flex: 1, minWidth: 140 }}
          disabled={disabled}
        />
      </Box>
      <Typography variant="subtitle2">Shwapno receiving account</Typography>
      {shwapnoAccounts.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No Shwapno receiving accounts are configured (same list as the mobile app).
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {shwapnoAccounts.map((account) => {
            const selectedId =
              String(proof.shwapnoPaymentSettingsId) === String(account.id)
            return (
              <Chip
                key={account.id}
                label={
                  account.label ||
                  `${account.bank_name || account.mfs_name || 'Account'} · ${account.account_number || account.contact_number || ''}`
                }
                onClick={() => onPatch(index, { shwapnoPaymentSettingsId: account.id })}
                color={selectedId ? 'primary' : 'default'}
                variant={selectedId ? 'filled' : 'outlined'}
                disabled={disabled}
              />
            )
          })}
        </Box>
      )}
      {fieldErrors.shwapnoPaymentSettingsId ? (
        <Typography variant="caption" color="error">
          {fieldErrors.shwapnoPaymentSettingsId}
        </Typography>
      ) : null}
    </Box>
  )
}

export default function PaymentProofEditAfterRejectDialog({
  open,
  orderId,
  requestId,
  onClose,
  onSaved,
}) {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [proofs, setProofs] = useState([])
  const [banks, setBanks] = useState([])
  const [shwapnoAccounts, setShwapnoAccounts] = useState([])
  const [showFieldErrors, setShowFieldErrors] = useState(false)
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false)

  const requestClose = useCallback(() => {
    if (saving) return
    setDiscardConfirmOpen(true)
  }, [saving])

  const confirmDiscard = useCallback(() => {
    setDiscardConfirmOpen(false)
    onClose()
  }, [onClose])

  useEffect(() => {
    if (!open) {
      setShowFieldErrors(false)
      setDiscardConfirmOpen(false)
      return undefined
    }
    if (!requestId) return undefined
    let cancelled = false
    ;(async () => {
      try {
        setLoading(true)
        setError(null)
        const [bankRes, accountsRes] = await Promise.all([
          adminOrderService.getPaymentProofBanks(),
          adminOrderService.getPaymentProofReceivingAccounts(),
        ])
        let req = null
        if (orderId) {
          const orderRes = await adminOrderService.getOrderPayment(orderId)
          if (cancelled) return
          req = (orderRes?.payment_proof_detail?.requests || []).find(
            (r) => r.id === requestId,
          )
        } else {
          const depositRes = await adminOrderService.getAdminDepositDetail(requestId)
          if (cancelled) return
          req = (depositRes?.payment_proof_detail?.requests || []).find(
            (r) => r.id === requestId,
          )
        }
        if (!req || String(req.status).toUpperCase() !== 'REJECTED') {
          setError('This payment proof request is not rejected.')
          setProofs([])
          return
        }
        const apiProofs = req.proofs || []
        setProofs(apiProofs.map(pendingProofFromRejectedApiProof))
        setBanks(bankRes?.data || [])
        setShwapnoAccounts(
          mergeReceivingAccountsWithProofs(accountsRes?.data || [], apiProofs),
        )
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load payment proof')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, orderId, requestId])

  const patchProof = (index, patch) => {
    setProofs((prev) => prev.map((p, i) => (i === index ? { ...p, ...patch } : p)))
  }

  const handleSave = async () => {
    setShowFieldErrors(true)
    const validShwapnoIds = shwapnoAccounts.map((account) => account.id)
    const validationOptions = {
      validShwapnoPaymentSettingsIds: validShwapnoIds,
    }
    const blocking = proofs
      .map((p, i) => ({
        index: i,
        errors: validatePendingProof(p, new Date(), validationOptions),
      }))
      .filter((row) => Object.keys(row.errors).length > 0)
    if (blocking.length > 0) {
      const first = blocking[0].errors
      const detail = Object.values(first)[0]
      setError(
        detail
          ? `Proof ${blocking[0].index + 1}: ${detail}`
          : 'Complete all required fields for each proof.',
      )
      return
    }
    try {
      setSaving(true)
      setError(null)
      const payloads = proofs.map((p) => buildProofSubmitPayload(p))
      if (orderId) {
        await adminOrderService.editRejectedPaymentProofRequest(
          orderId,
          requestId,
          payloads,
        )
      } else {
        await adminOrderService.editRejectedPaymentProofRequestById(
          requestId,
          payloads,
        )
      }
      onSaved?.()
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to save'
      setError(Array.isArray(msg) ? msg.join(', ') : msg)
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
    <Dialog open={open} onClose={saving ? undefined : requestClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Edit rejected payment proof · Request #{requestId}
        <IconButton onClick={requestClose} disabled={saving} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}
        {loading ? (
          <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          proofs.map((proof, index) => (
            <ProofFields
              key={proof.file?.name || index}
              proof={proof}
              index={index}
              banks={banks}
              shwapnoAccounts={shwapnoAccounts}
              onPatch={patchProof}
              disabled={saving}
              showFieldErrors={showFieldErrors}
            />
          ))
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} color="inherit">Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading || !proofs.length}
          sx={{ bgcolor: '#11809A', '&:hover': { bgcolor: '#0f6d82' } }}
        >
          {saving ? 'Saving…' : 'Resubmit for verification'}
        </Button>
      </DialogActions>
    </Dialog>
    <Dialog
      open={discardConfirmOpen}
      onClose={() => setDiscardConfirmOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Discard payment proof?</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          Entered details and any uploaded files will be lost.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDiscardConfirmOpen(false)} color="inherit">
          Cancel
        </Button>
        <Button onClick={confirmDiscard} color="error" variant="contained">
          Discard
        </Button>
      </DialogActions>
    </Dialog>
    </>
  )
}
